import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { POSTS, getPostBySlug } from "@/data/journal";
import { useSeo } from "@/lib/seo";

function JsonLd({ post }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta.description,
    image: [post.image],
    datePublished: post.date,
    author: { "@type": "Organization", name: "Golf Start" },
    publisher: {
      "@type": "Organization",
      name: "Golf Start",
      url: "https://mygolfstart.com",
    },
    articleSection: post.category,
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

export default function JournalPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  // Always call hooks before any conditional return
  useSeo({
    title: post?.meta.title || "Journal · Golf Start",
    description: post?.meta.description || "",
    // When a slug is unknown we'll redirect to /journal — canonical should
    // point to /journal in that fallback case so Google doesn't index 404s.
    path: post ? `/journal/${slug}` : "/journal",
  });

  if (!post) return <Navigate to="/journal" replace />;

  const related = POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-cream" data-testid="journal-post-page">
      <JsonLd post={post} />
      <Header />

      <article className="mx-auto max-w-[860px] px-6 md:px-12 py-16 md:py-24">
        <Link
          to="/journal"
          data-testid="journal-back-link"
          className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.25em] uppercase text-forest-muted hover:text-gold transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
          Back to the Journal
        </Link>

        <header className="mt-10">
          <div className="flex items-center gap-4 text-xs tracking-[0.25em] uppercase text-forest-muted">
            <span className="text-gold">{post.category}</span>
            <span className="h-px w-6 bg-cream-border" />
            <span>{post.readTime}</span>
            <span className="h-px w-6 bg-cream-border" />
            <span>{post.date}</span>
          </div>
          <h1 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.05] text-forest">
            {post.title}
          </h1>
          <p className="mt-6 font-serif italic text-xl md:text-2xl leading-relaxed text-forest-muted">
            {post.excerpt}
          </p>
        </header>

        <div className="mt-12 relative aspect-[16/9] overflow-hidden bg-forest">
          <img
            src={post.image}
            alt={post.title}
            className="h-full w-full object-cover"
          />
        </div>

        <div className="mt-14 font-sans text-lg leading-[1.75] text-forest-dark space-y-10">
          <p className="font-serif text-xl md:text-2xl leading-[1.55] text-forest first-letter:font-serif first-letter:text-7xl first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-none first-letter:text-gold">
            {post.content.intro}
          </p>

          {post.content.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-serif text-2xl md:text-3xl text-forest mt-12 mb-5 leading-snug">
                {s.heading}
              </h2>
              {s.paragraphs.map((p, j) => (
                <p key={j} className="mb-5 text-forest-dark leading-[1.75]">
                  {p}
                </p>
              ))}
              {s.list && (
                <ul className="my-6 space-y-3 pl-0">
                  {s.list.map((item, k) => (
                    <li key={k} className="flex gap-4 items-start">
                      <span className="font-serif text-gold text-xl leading-none mt-1">·</span>
                      <span className="text-forest-dark leading-[1.7]">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.pullquote && (
                <blockquote className="my-10 border-l-2 border-gold pl-6 md:pl-8 font-serif italic text-2xl md:text-3xl leading-[1.35] text-forest">
                  {s.pullquote}
                </blockquote>
              )}
            </section>
          ))}

          <div className="mt-14 pt-10 border-t border-cream-border">
            <div className="eyebrow mb-4">The Takeaway</div>
            <p className="font-serif text-xl md:text-2xl leading-[1.55] text-forest">
              {post.content.takeaway}
            </p>
          </div>
        </div>

        {/* Coach CTA */}
        <aside className="mt-16 p-8 md:p-10 bg-forest text-cream-light">
          <div className="eyebrow text-gold">Next Step</div>
          <h3 className="mt-3 font-serif text-3xl md:text-4xl leading-tight">
            Put it into practice with a coach.
          </h3>
          <p className="mt-4 font-sans text-cream-light/80 max-w-xl leading-relaxed">
            Tips are a start. A vetted coach watching you swing is the real shortcut. Browse the
            Golf Start roster — every coach is certified and reviewed.
          </p>
          <Link
            to="/coaches"
            data-testid="post-cta-find-coach"
            className="mt-7 inline-flex items-center gap-2 bg-gold text-forest px-7 py-4 font-sans text-xs tracking-[0.25em] uppercase hover:bg-cream-light transition-colors"
          >
            Find Coaches
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </Link>
        </aside>
      </article>

      {/* Related */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-16 md:py-24 border-t border-cream-border">
        <div className="eyebrow mb-8">Keep Reading</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {related.map((p) => (
            <Link
              key={p.slug}
              to={`/journal/${p.slug}`}
              data-testid={`journal-related-${p.slug}`}
              className="group flex flex-col bg-cream-light border border-cream-border overflow-hidden transition-transform duration-500 ease-out hover:-translate-y-1 hover:border-gold"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-forest">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
              </div>
              <div className="p-6">
                <div className="eyebrow text-gold">{p.category}</div>
                <h4 className="mt-3 font-serif text-xl leading-tight text-forest group-hover:text-gold transition-colors">
                  {p.title}
                </h4>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
